<template>
  <div class="wf_container">
    <template v-if="fetchingWorkflow">
      <div class="header_skeleton">
        <v-skeleton-loader class="skeleton" type="text" />
      </div>
      <div class="body_section">
        <div v-for="value in 4" :key="value" class="status_skeleton">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
      </div>
    </template>
    <template v-else-if="workflow">
      <div class="header_title" v-text="workflow.name" />
      <div class="filter_section">
        <WorkflowFolderCollaboratorFilter
          v-model="selectedCollaboratorId"
          :organization-id="organizationId"
          :skip-default="collaboratorInitialized"
          @init="onCollaboratorInit"
        />
        <WorkflowFolderContactFilter
          v-model="selectedContactId"
          :organization-id="organizationId"
        />
        <BaseButtonIcon
          icon="$mdiClose"
          @click="clearAllFilters"
        />
        <v-spacer />
        <v-text-field
          v-model="searchCriteria"
          outlined
          class="filter_search"
          :label="'Rechercher un dossier'"
          prepend-inner-icon="$mdiMagnify"
          dense
          hide-details
          autofocus
        />
      </div>
      <div v-if="!hideStatus" class="body_section">
        <WorkflowFolderStatus
          v-for="(status, index) in workflow.statuses"
          :key="index"
          :status="status"
          :wf-statuses="workflow.statuses"
          :search-criteria="searchCriteria"
          :filter-by-contact="selectedContactId"
          :filter-by-collaborator="selectedCollaboratorId"
          @openRelanchMass="handleOpenRelanchMass($event)"
        />
      </div>

      <!-- MODAL SECTION -->
      <WorkflowFolderRelaunchDialog
        v-if="openRelanchMass"
        :status-id="activeStatus.id"
        :wf-statuses="workflow.statuses"
        @reloadStatusFolder="reloadStatusFolder()"
        @close="handleCloseRelanchMass()"
      />
    </template>
  </div>
</template>
<script>
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";
import Workflow from "@/core/Models/Workflow";
import WorkflowFolderStatus from "@/modules/Admin/Components/workflow-folder/WorkflowFolderStatus.vue";
import WorkflowFolderRelaunchDialog from "@/modules/Admin/Components/workflow-folder/WorkflowFolderRelaunchDialog.vue";
import WorkflowFolderCollaboratorFilter from "@/modules/Admin/Components/workflow-folder/WorkflowFolderCollaboratorFilter.vue";
import WorkflowFolderContactFilter from "@/modules/Admin/Components/workflow-folder/WorkflowFolderContactFilter.vue";

export default {
  name: "WorkflowFolder",
  components: {
    WorkflowFolderStatus,
    WorkflowFolderRelaunchDialog,
    WorkflowFolderCollaboratorFilter,
    WorkflowFolderContactFilter,
  },
  props: {
    workflowId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    const { name, collaborator, contact } = this.$route.query;
    const collaboratorInUrl = collaborator !== undefined;
    return {
      hideStatus: false,
      fetchingWorkflow: true,
      workflow: null,
      activeStatus: null,
      openRelanchMass: false,
      searchInput: null,
      searchCriteria: name || null,
      selectedContactId: contact ? Number(contact) : null,
      selectedCollaboratorId: collaboratorInUrl
        ? (collaborator === "all" ? null : Number(collaborator))
        : null,
      defaultCollaboratorId: null,
      collaboratorInitialized: collaboratorInUrl,
    };
  },
  computed: {
    hasActiveFilters() {
      return !!this.searchCriteria || this.selectedContactId !== null || this.selectedCollaboratorId !== null;
    },
    filtersQuery() {
      if (!this.collaboratorInitialized) return null;
      const query = {};
      if (this.searchCriteria) query.name = this.searchCriteria;
      if (this.selectedContactId !== null) query.contact = String(this.selectedContactId);
      query.collaborator = this.selectedCollaboratorId === null ? "all" : String(this.selectedCollaboratorId);
      return query;
    },
  },
  watch: {
    filtersQuery(query) {
      if (query === null) return;
      this.$router.replace({ query }).catch(() => {});
    },
  },
  created() {
    this.fetchWorkflow();
  },
  methods: {
    onCollaboratorInit(id) {
      this.defaultCollaboratorId = id;
      if (!this.collaboratorInitialized) {
        this.selectedCollaboratorId = id;
      }
      this.collaboratorInitialized = true;
    },
    clearAllFilters() {
      this.searchCriteria = null;
      this.selectedContactId = null;
      this.selectedCollaboratorId = this.defaultCollaboratorId;
    },
    async fetchWorkflow() {
      this.fetchingWorkflow = true;
      try {
        const res = await WorkflowAPI.getOrganizationWorkflow(
          this.organizationId,
          this.workflowId
        );
        this.workflow = new Workflow(res.data);
        this.setBreadcrumbLabel();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.fetchingWorkflow = false;
    },
    setBreadcrumbLabel() {
      this.$route.meta.breadcrumbs[0].label = `${this.workflow.name} `;
    },
    handleOpenRelanchMass(status) {
      this.activeStatus = status;
      this.openRelanchMass = true;
    },
    handleCloseRelanchMass() {
      this.activeStatus = null;
      this.openRelanchMass = false;
    },
    reloadStatusFolder() {
      this.hideStatus = true;
      this.$nextTick(() => {
        this.hideStatus = false;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.wf_container {
  width: 100%;
  height: 100%;
  .header_section {
    width: 100%;
    padding: 20px;
    border: 0.5px solid #ecebf1;
    background: #fff;
    overflow: hidden;
    margin-bottom: 16px;
    height: 60px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .header_title {
    flex-grow: 2;
    font-weight: 600;
    font-size: 20px;
    line-height: 20px;
    text-transform: capitalize;
    margin-bottom: 16px;
  }
  .filter_section {
    width: 100%;
    padding: 8px 20px;
    border: 0.5px solid #ecebf1;
    background: #fff;
    margin-bottom: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    .filter_search {
      max-width: 280px;
    }
  }
  .body_section {
    width: 100%;
    height: calc(100% - 152px);
    display: flex;
    gap: 16px;
    overflow-y: auto;
  }
  .header_skeleton {
    width: 100%;
    margin-bottom: 16px;
    height: 60px;
  }
  .skeleton {
    height: 100%;
    width: 100%;
    ::v-deep {
      .v-skeleton-loader__bone {
        height: 100%;
      }
    }
  }
  .status_skeleton {
    width: 350px;
    height: 100%;
  }
}
</style>
